// Safari's "Prevent cross-site tracking" (ITP) blocks cookies for a framed origin
// unless the user has recently used that origin as a first-party site. Polly's pricer
// runs off its own session cookie, so on Safari the iframe can "load" - onload fires
// normally - and still sit blank or bounce through a sign-in it can never complete.
//
// Nothing in the platform reports this. The frame is cross-origin so we cannot read its
// document, and there is no API that says "ITP blocked this". So this is a heuristic,
// and it is deliberately split into two narrow checks that have to agree rather than one
// broad "is this broken" guess:
//
//   1. isWebKitBrowser()          - is this even an engine that enforces ITP?
//   2. a load deadline (component) - did the frame ever show a sign of life?
//
// Neither is conclusive alone. Because the result is still a guess, the UI it drives
// overlays a dismissible panel instead of replacing the frame, so a false positive
// costs the user one click rather than access to a working pricer.

const DEBUG_PREFIX = '[SafariFrameNotice]';

export const SAFARI_FRAME_NOTICE_FLAG_KEY = 'swift.safariFrameNotice';

// Default OFF, so this ships dark. Two ways to turn it on:
//   - flip this to true for everyone, or
//   - run localStorage.setItem('swift.safariFrameNotice', 'on') in a real Safari
//     session to try it without a rebuild.
// localStorage 'off' always wins, so it can be killed per-machine even once this is true.
export const SAFARI_FRAME_NOTICE_ENABLED = false;

export function frameNoticeDebug(...args) {
  try {
    if (safariFrameNoticeEnabled()) console.debug(DEBUG_PREFIX, ...args);
  } catch (_) { /* console is not worth throwing over */ }
}

export function safariFrameNoticeEnabled() {
  let override = null;
  try {
    override = window.localStorage.getItem(SAFARI_FRAME_NOTICE_FLAG_KEY);
  } catch (_) {
    // Private mode / blocked storage. Fall through to the compiled default.
  }
  if (override === 'on') return true;
  if (override === 'off') return false;
  return SAFARI_FRAME_NOTICE_ENABLED;
}

export function isWebKitBrowser() {
  if (typeof navigator === 'undefined') return false;

  // Every browser on iOS and iPadOS is WKWebView underneath and inherits ITP, and all of
  // them report Apple as the vendor - as does desktop Safari. Chrome, Edge and Firefox on
  // desktop report 'Google Inc.' or ''. That makes vendor a cleaner signal than the user
  // agent, which Safari's own string ("...Chrome-ish...Safari") makes needlessly painful.
  if (navigator.vendor === 'Apple Computer, Inc.') return true;

  const ua = navigator.userAgent || '';
  return /^((?!chrome|chromium|android|crios|fxios|edgios).)*safari/i.test(ua);
}

// A pricer launch url is single-use: it's a one-time sign-in link, so once the iframe has
// loaded it, opening that same url in a tab lands on an expired session. So the tab always
// gets a url minted just for it.
//
// The tab is opened synchronously, inside the click, and pointed at the new url once it
// comes back. Opening it after the await instead would lose the "user clicked" status and
// Safari - the one browser this button exists for - would block it as a popup.
export async function openFreshUrlInTab(getUrl, label = 'the Pricing Engine') {
  let win = null;
  try {
    win = window.open('', '_blank');
  } catch (_) {
    win = null;
  }

  if (win) {
    // Cut the link back to this page before it navigates anywhere (what 'noopener' did).
    try { win.opener = null; } catch (_) { /* some browsers make it read-only; harmless */ }
    try {
      win.document.title = 'Opening ' + label + '...';
      win.document.body.style.cssText = 'font:14px system-ui,sans-serif;color:#46566f;padding:24px';
      win.document.body.textContent = 'Opening ' + label + '...';
    } catch (_) { /* cosmetic only */ }
  }

  try {
    const url = await getUrl();
    if (!url) throw new Error('No launch URL came back.');
    frameNoticeDebug('opening fresh launch url in a new tab', { reusedBlankTab: !!win });
    if (win && !win.closed) {
      win.location.replace(url);
    } else {
      // Blank tab was blocked or closed; last try. May itself be blocked as a popup.
      window.open(url, '_blank', 'noopener');
    }
  } catch (err) {
    if (win && !win.closed) win.close();
    throw err;
  }
}
